import { ConfigurationDomain } from 'constants/ConfigurationDomain';
import {
  CONTRACT_COMPLIANCE__BREAK_HOURS_FOR_MINORS,
  CONTRACT_COMPLIANCE__NIGHT_WORK_FOR_MINORS,
  MANAGEMENT__ABSENCE,
  MANAGEMENT__CONTRACT_VALIDATION_COMPLIANCE,
  MANAGEMENT__FEATURE_ACCESS,
  MANAGEMENT__JOINT_COMMISSIONS,
  MANAGEMENT__PERMISSIONS,
  MANAGEMENT__SECURITY_AUTHENTICATION,
  MANAGEMENT__TIME_REGISTRATION_POLICY,
} from 'translations/constants';
import { ContractComplianceTab } from './contract-compliance/routes';
import { IConfigurationDomain } from './types';

export const MANAGEMENT_ROUTE = '/settings/management';

export const CONFIGURATION_DOMAINS: IConfigurationDomain[] = [
  { key: ConfigurationDomain.FEATURE_ACCESS, labelKey: MANAGEMENT__FEATURE_ACCESS },
  { key: ConfigurationDomain.TIME_REGISTRATION_POLICY, labelKey: MANAGEMENT__TIME_REGISTRATION_POLICY },
  { key: ConfigurationDomain.ABSENCE, labelKey: MANAGEMENT__ABSENCE },
  { key: ConfigurationDomain.JOINT_COMMISSIONS, labelKey: MANAGEMENT__JOINT_COMMISSIONS },
  { key: ConfigurationDomain.SECURITY_AUTHENTICATION, labelKey: MANAGEMENT__SECURITY_AUTHENTICATION },
  {
    key: ConfigurationDomain.CONTRACT_VALIDATION_COMPLIANCE,
    labelKey: MANAGEMENT__CONTRACT_VALIDATION_COMPLIANCE,
    subSections: [
      { key: ContractComplianceTab.NIGHT_WORK_FOR_MINORS, labelKey: CONTRACT_COMPLIANCE__NIGHT_WORK_FOR_MINORS },
      { key: ContractComplianceTab.BREAK_HOURS_FOR_MINORS, labelKey: CONTRACT_COMPLIANCE__BREAK_HOURS_FOR_MINORS },
    ],
  },
  { key: ConfigurationDomain.PERMISSIONS, labelKey: MANAGEMENT__PERMISSIONS },
];

/** Domain the section root redirects to. */
export const DEFAULT_CONFIGURATION_DOMAIN = ConfigurationDomain.FEATURE_ACCESS;

export const buildDomainRoute = (key: ConfigurationDomain): string => `${MANAGEMENT_ROUTE}/${key}`;

export const isConfigurationDomain = (value?: string): value is ConfigurationDomain =>
  CONFIGURATION_DOMAINS.some(domain => domain.key === value);

/**
 * Reads the domain segment out of `/settings/management/<domain>/<sub-section>`.
 *
 * The shell is the element of the parent route `/settings/management`, so `useParams()` there
 * does not see the child route's `:domainKey`. Parsing the pathname is how UPTab already
 * resolves its active section (see components/tab/UPTab.tsx), so this follows existing practice.
 *
 * Returns undefined at the section root.
 */
export const getDomainKeyFromPath = (pathname: string): string | undefined => pathname.split('/')[3] || undefined;

/** Reads the sub-section segment out of `/settings/management/<domain>/<sub-section>`; undefined when absent. */
export const getSubSectionKeyFromPath = (pathname: string): string | undefined => pathname.split('/')[4] || undefined;

export const buildSubSectionRoute = (domainKey: ConfigurationDomain, subSectionKey: string): string =>
  `${buildDomainRoute(domainKey)}/${subSectionKey}`;

export const getConfigurationDomain = (key: ConfigurationDomain): IConfigurationDomain | undefined =>
  CONFIGURATION_DOMAINS.find(domain => domain.key === key);
